import React, { useEffect, useRef, useState } from 'react';
import { formSaveOf, formsOpenAt, subscribeFormSaves } from '../../../tikit/List/formSaves';
import { CELL_BOX, markClass, savingCell, useSaveMark } from './inlineCell';
import { PillList } from './PillList';
import { EMPTY_CELL, hoverTitle } from './emptyCell';

interface FormQuestionCellProps {
  ticketId: number;
  columnKey: number;
  label: string;
  text: string;
  /** A multi-choice answer's picks, drawn as pills (PillList) in place of the text. */
  pills?: string[];
  onOpen: () => void;
}

/**
 * A form question's answer in a list cell that opens the ticket's forms (FormAnswersDialog), and shows that dialog's
 * save as the other editable cells show theirs: dim then pulse while it is out, then the green or red box (formSaves).
 */
export const FormQuestionCell = ({ ticketId, columnKey, label, text, pills, onOpen }: FormQuestionCellProps) => {
  const [, rerender] = useState(0);
  // Again for each ticket: the grid reuses a cell for another ticket when a reload reorders the rows.
  useEffect(() => subscribeFormSaves(ticketId, () => rerender(n => n + 1)), [ticketId]);

  const save = formSaveOf(ticketId);
  // The grid does not redraw rows for the list's own state, so the open dialog is read from formSaves too.
  const openAt = formsOpenAt();
  const open = !!openAt && openAt.ticketId === ticketId && openAt.columnKey === columnKey;
  const mine = save?.keys.has(columnKey) ? save : undefined;
  const saveMark = useSaveMark(ticketId, text, mine?.phase === 'saving', `form:${columnKey}`);

  // An outcome is shown once; a cell drawn after it (a reload re-keys the rows) does not show it again.
  const shown = useRef(mine && mine.phase !== 'saving' ? mine.version : undefined);
  useEffect(() => {
    if (!mine || mine.phase === 'saving' || shown.current === mine.version) return;
    shown.current = mine.version;
    saveMark.show(mine.phase === 'saved' ? 'saved' : 'refused', mine.target === columnKey);
  }, [mine?.version]);

  const lock = savingCell(saveMark.busy);
  const openForms = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    saveMark.opening();
    onOpen();
  };
  return (
    <div
      className={`inline-edit-cell${markClass(saveMark.mark, saveMark.edited)}${lock.className}`}
      {...lock.props}
      data-selection-disabled={true}
      tabIndex={0}
      role="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`${label}: ${text}`}
      {...(pills ? {} : hoverTitle(text))}
      onMouseDown={event => event.stopPropagation()}
      onClick={openForms}
      onKeyDown={event => { if (event.key === 'Enter' || event.key === 'F2') { event.preventDefault(); openForms(event); } }}
      style={{ ...CELL_BOX, cursor: 'text', ...saveMark.markStyle }}
    >
      {pills ? <PillList names={pills} title={label} /> : <span className='truncate'><bdi>{text || EMPTY_CELL}</bdi></span>}
    </div>
  );
};
